<!-- 
HACCP情報ユーザーの一覧画面
@props
  itemList : ユーザー一覧として表示するユーザー情報の配列
  canAddUser : ユーザーを追加できる状態であればtrue、そうでなければfalseを指定する
-->

<template>
  <div>
    <div class="ml-2 text-h5">アカウント管理</div>
    <v-data-table
      locale="jp"
      mobile-breakpoint="0"
      v-model="selected"
      :headers="headers"
      :items="itemList"
      item-class="text-truncate"
      :custom-sort="sortFunction"
      item-key="uid"
      show-select
      hide-default-footer
      :single-select="false"
      :search="searchCondition"
      :custom-filter="executeSearch"
      :page.sync="page"
      :items-per-page="itemsPerPage"
      @page-count="pageCount = $event"
      no-results-text="検索条件に合うデータがありません"
      no-data-text="データがありません"
    >
      <!-- テーブル上部のボタン -->
      <template v-slot:top>
        <v-btn
          color="primary"
          class="ma-2"
          @click="create"
          :disabled="!canAddUser"
        >
          アカウント追加
        </v-btn>
        <v-menu bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              class="ma-2"
              v-bind="attrs"
              v-on="on"
              :disabled="!selectionStatus.includes('contain.any')"
            >
              操作 <v-icon>mdi-chevron-down</v-icon></v-btn
            >
          </template>
          <v-list>
            <v-list-item
              v-for="(item, index) in items"
              :key="index"
              :disabled="!selectionStatus.includes(item.condition)"
              @click="item.event"
            >
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-row class="mb-0" align="center">
          <v-col cols="auto" sm="8" md="5" lg="3" xl="3">
            <v-text-field
                v-model="search"
                class="mx-4"
                outlined
                dense
                hide-details
                @keyup.enter.exact="requestToSearch"
            >
              <template v-slot:prepend>
                <v-icon>mdi-magnify</v-icon>
              </template>
              <template v-slot:append-outer>
                <v-btn
                    outlined
                    color="primary"
                    style="margin-top: -6px"
                    @click="requestToSearch"
                >検索</v-btn
                >
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </template>
      <!-- ヘッダ -->
      <template v-slot:[`header.data-table-select`]="{ props, on }">
        <v-simple-checkbox
          :value="props.value || props.indeterminate"
          v-on="on"
          :indeterminate="props.indeterminate"
          dark
          :ripple="false"
        />
      </template>
      <template
        v-slot:[`item.data-table-select`]="{ item, isSelected, select }"
      >
        <v-simple-checkbox
          v-if="item.accountEdit.editable"
          :value="isSelected"
          @input="select($event)"
          :ripple="false"
        >
        </v-simple-checkbox>
      </template>
      <template v-slot:[`header.group`]>
        <all-open-button
          :title="headers[2].text"
          @on-change-open-state="onChangeJoinGroup"
        ></all-open-button>
      </template>
      <!-- テーブル内のセル -->
      <template v-slot:[`header.permissions`]="{}">
        <all-open-button
          :title="headers[3].text"
          @on-change-open-state="onChangePermissionGroup"
        ></all-open-button>
      </template>
      <template v-slot:[`item.userName`]="{ item }">
        <td class="fixed-width-200 text-truncate">{{ item.userName }}</td>
      </template>
      <template v-slot:[`item.mailAddress`]="{ item }">
        <td class="fixed-width-300 text-truncate">{{ item.mailAddress }}</td>
      </template>
      <template v-slot:[`item.group`]="{ item }">
        <tooltips
          :ref="'joinTooltips.' + item.uid"
          :data="item.group"
          :max="3"
          class="fixed-width-300"
        ></tooltips>
      </template>
      <template v-slot:[`item.permissions`]="{ item }">
        <tooltips
          :ref="'permissionTooltips.' + item.uid"
          :data="item.permissions"
          :max="3"
          class="fixed-width-300"
        ></tooltips>
      </template>
      <template v-slot:[`item.accountEdit`]="{ item }">
        <v-chip
          color="primary"
          dark
          label
          v-if="item.accountEdit.editable"
          @click.stop="edit(item.uid)"
        >
          編集
        </v-chip>
      </template>
    </v-data-table>
    <div class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="7"
      ></v-pagination>
    </div>
    <!-- ダイアログ -->
    <confirm-dialog ref="confirm_account_restart" @on-click-ok="restartAccount">
      アカウントを再開します。よろしいですか？
    </confirm-dialog>
    <confirm-dialog ref="confirm_account_stop" @on-click-ok="stopAccount">
      アカウントを停止します。よろしいですか？
    </confirm-dialog>
    <confirm-dialog ref="confirm_change_pasword" @on-click-ok="passwordChange">
      パスワードを再発行します。よろしいですか？
    </confirm-dialog>
    <confirm-dialog ref="confirm_account_delete" @on-click-ok="deleteAccount">
      アカウントを削除します。よろしいですか？
    </confirm-dialog>
  </div>
</template>

<script>
import ConfirmDialog from "../dialog/confirm.vue";
import Tooltips from "../common/tooltips.vue";
import AllOpenButton from "../common/all-open-button.vue";
import valueConverter from "../../logic/common/value-converter";
export default {
  props: ["itemList", "canAddUser"],
  components: {
    "confirm-dialog": ConfirmDialog,
    tooltips: Tooltips,
    "all-open-button": AllOpenButton,
  },
  computed: {
    /**
     * 各チェックボックスの選択状態
     */
    selectionStatus() {
      const selectedAccountList = this.selectedAccountList;
      if (selectedAccountList.length == 0) {
        return [];
      }

      let result = ["contain.any"];
      if (selectedAccountList.every((user) => user.status == "inactive")) {
        result.push("contain.inactive");
      }
      if (selectedAccountList.every((user) => user.status == "active")) {
        result.push("contain.active");
      }
      return result;
    },
    /**
     * 選択されたアカウントの一覧
     */
    selectedAccountList() {
      return this.selected.filter((item) => item.accountEdit.editable);
    },
  },
  data() {
    return {
      // 検索条件（入力中）
      search: "",
      // 検索条件（一覧反映中）
      searchCondition: "",
      // 表示中のページ番号
      page: 1,
      // ページ数
      pageCount: 0,
      // ページあたりに表示するユーザー件数
      itemsPerPage: 20,
      // 選択中のユーザー情報
      selected: [],
      // 一覧のヘッダ定義
      headers: [
        {
          text: "ユーザー名",
          align: "start",
          value: "userName",
          class: "grey darken-1 white--text",
          width: "200px",
        },
        {
          text: "メールアドレス",
          align: "start",
          value: "mailAddress",
          class: "grey darken-1 white--text",
          width: "300px",
        },
        {
          text: "所属グループ",
          align: "start",
          value: "group",
          class: "grey darken-1 white--text pl-30",
          sortable: false,
          width: "50%",
        },
        {
          text: "グループ権限",
          align: "start",
          value: "permissions",
          class: "grey darken-1 white--text pl-30",
          sortable: false,
          width: "50%",
        },
        {
          text: "最終ログイン",
          align: "start",
          value: "lastLogin",
          class: "grey darken-1 white--text",
          width: "150px",
        },
        {
          text: "アカウント編集",
          align: "start",
          value: "accountEdit",
          class: "grey darken-1 white--text",
          sortable: false,
          width: "100px",
        },
      ],
      // 画面上部の「操作」で表示する操作ボタンの定義
      items: [
        {
          title: "アカウント再開",
          condition: "contain.inactive",
          event: () => {
            this.$refs.confirm_account_restart.showDialog();
          },
        },
        {
          title: "アカウント停止",
          condition: "contain.active",
          event: () => {
            this.$refs.confirm_account_stop.showDialog();
          },
        },
        {
          title: "パスワード再発行",
          condition: "contain.any",
          event: () => {
            this.$refs.confirm_change_pasword.showDialog();
          },
        },
        {
          title: "アカウント削除",
          condition: "contain.any",
          event: () => {
            this.$refs.confirm_account_delete.showDialog();
          },
        },
      ],
    };
  },
  methods: {
    /**
     * アカウントを作成する
     */
    create() {
      this.$router.push("/account/edit/create");
    },
    /**
     * アカウントを編集する
     */
    edit(uid) {
      this.$router.push(`/account/edit/${uid}`);
    },
    /**
     * 所属グループのすべて開く/すべて閉じるを押下した
     */
    onChangeJoinGroup(state) {
      Object.keys(this.$refs)
        .filter((key) => key.startsWith("joinTooltips."))
        .forEach((key) => {
          const item = this.$refs[key];
          if (item) {
            item.setEllipsisState(!state);
          }
        });
    },
    /**
     * 権限グループのすべて開く/すべて閉じるを押下した
     */
    onChangePermissionGroup(state) {
      Object.keys(this.$refs)
        .filter((key) => key.startsWith("permissionTooltips."))
        .forEach((key) => {
          const item = this.$refs[key];
          if (item) {
            item.setEllipsisState(!state);
          }
        });
    },
    /**
     * 並び替えをカスタマイズする
     */
    sortFunction(items, sortBy, sortDesc) {
      if (sortBy.length == 0) {
        const userId = this.$store.getters.userId;
        return valueConverter.dataTableCustomSort(
          items,
          ["userName"],
          [false],
          (a, b) => {
            // ログインユーザーと同じIDであれば先頭に表示する
            if (userId == a.uid) {
              return -1;
            }
            if (userId == b.uid) {
              return 1;
            }
            // ログインユーザー以外のユーザーはデフォルトの比較方法で判定を行う
            return undefined;
          }
        );
      }
      return valueConverter.dataTableDefaultSort(items, sortBy, sortDesc);
    },
    /**
     * アカウントを削除する
     */
    deleteAccount() {
      this.$emit("on-delete-account", this.selectedAccountList);
      this.selected = [];
    },
    /**
     * パスワードを再設定する
     */
    passwordChange() {
      this.$emit("on-password-change", this.selectedAccountList);
      this.selected = [];
    },
    /**
     * アカウントを停止する
     */
    stopAccount() {
      this.$emit("on-stop-account", this.selectedAccountList);
      this.selected = [];
    },
    /**
     * アカウントを再開する
     */
    restartAccount() {
      this.$emit("on-restart-account", this.selectedAccountList);
      this.selected = [];
    },
    /**
     * 検索条件を一覧に反映する
     */
    requestToSearch() {
      this.searchCondition = this.search;
    },
    /**
     * 検索を実施する
     */
    executeSearch(_, search, item) {
      if (search == null) {
        return true;
      }
      // ユーザー名で部分一致検索する
      const userName = item.userName;
      // メールアドレスで部分一致検索する
      const mailAddress = item.mailAddress;
      // グループ名で部分一致検索する
      const groups = item.group.map((g) => g.name).join(" ");
      // ユーザー名で部分一致検索する
      const permissions = item.permissions.map((p) => p.name).join(" ");
      // 最終更新日時で部分一致検索する
      const lastLogin = item.lastLogin;
      if (userName.includes(search)) {
        return true;
      }
      if (mailAddress.includes(search)) {
        return true;
      }
      if (lastLogin.includes(search)) {
        return true;
      }
      if (groups.includes(search)) {
        return true;
      }
      if (permissions.includes(search)) {
        return true;
      }
      return false;
    },
  },
};
</script>

<style>
th {
  background-color: #757575 !important;
}
th.sortable .v-data-table-header__icon {
  color: rgb(255, 255, 255, 0.15) !important;
}
th.sortable.active .v-data-table-header__icon {
  color: rgb(255, 255, 255, 1) !important;
}
</style>
